import { createStore } from '@orderx/lab/xr/store';
import { useAppCacheAutoSync, AppCache } from '../services/app-cache';
import { useEffect } from 'react';
import { utilLog } from '../util/utilLog';

const CACHE_KEY = 'geolocation';

export enum GeolocationPermissionStatus {
    PROMPT = 'prompt',
    GRANTED = 'granted',
    DENIED = 'denied',
}

interface Store {
    status?: GeolocationPermissionStatus;
    coordinates?: GeolocationCoordinates;
}

const { useStore } = createStore<Store>(
    Object.assign(
        {
            status: GeolocationPermissionStatus.PROMPT,
        },
        AppCache.get(CACHE_KEY),
    ),
);

export function useGeolocation() {
    const [state, setState] = useStore();

    useAppCacheAutoSync(CACHE_KEY, state);

    function requestLocation() {
        getCurrentPosition()
            .then((result) => {
                utilLog('[useGeolocation] getCurrentPosition success', { result });

                setState((store) => {
                    store.status = GeolocationPermissionStatus.GRANTED;
                    store.coordinates = result.coords;
                });
            })
            .catch((error) => {
                utilLog('[useGeolocation] getCurrentPosition error', { error });

                if (error.code === 1) {
                    setState((store) => {
                        store.status = GeolocationPermissionStatus.DENIED;
                    });
                }
            });
    }

    return {
        ...state,
        canRequest:
            state.status === GeolocationPermissionStatus.PROMPT &&
            (window.location.protocol === 'https:' || window.location.hostname === 'localhost'),
        requestLocation,
        useRegister() {
            const status = state.status;

            useEffect(() => {
                if (status === GeolocationPermissionStatus.GRANTED) {
                    requestLocation();
                }
            }, [status]);
        },
    };
}

function getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition((result) => resolve(result), reject, {
            enableHighAccuracy: true,
        }),
    );
}
